import { useEffect, useState } from 'react';
import { api } from './api';
import { Notice } from './components';
import type { PendingBatch } from './types';

/**
 * Choose which accounts from an Instagram or LinkedIn login belong to this
 * client.
 *
 * A single login usually manages several clients' pages, and every one of
 * them comes back from OAuth. Nothing is attached until the operator ticks
 * it here; accounts already belonging to another client can't be ticked.
 */
export function AccountPicker({
  pendingId,
  slug,
  onDone,
}: {
  pendingId: string;
  slug: string;
  onDone: (message: string | null) => void;
}) {
  const [batch, setBatch] = useState<PendingBatch | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .getPending(pendingId)
      .then((loaded) => {
        setBatch(loaded);
        // Pre-tick only when exactly one account is available, so the common
        // single-account case is one click -- never pre-tick from a list.
        const free = loaded.accounts.filter((a) => a.linked_client_slug === null);
        if (loaded.accounts.length === 1 && free.length === 1) {
          setChosen(new Set([free[0]!.external_id]));
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [pendingId]);

  function toggle(id: string) {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const connections = await api.confirmPending(pendingId, [...chosen]);
      const names = connections.map((c) => c.external_name ?? c.external_id).join(', ');
      onDone(`Connected ${names}. Run a backfill to pull its history.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setBusy(false);
    }
  }

  async function cancel() {
    await api.discardPending(pendingId).catch(() => undefined);
    onDone(null);
  }

  const platformName = batch?.platform === 'linkedin' ? 'LinkedIn Pages' : 'Instagram accounts';
  const wrongClient = batch !== null && batch.client_slug !== slug;

  return (
    <div className="card" style={{ borderColor: 'var(--accent)' }}>
      <h2>Choose which {platformName} belong to this client</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        Your login can see every account below. Tick only the ones that belong to this client
        &mdash; the others stay unconnected.
      </p>

      {error && <Notice kind="err">{error}</Notice>}
      {wrongClient && (
        <Notice kind="err">This connection request was started for a different client.</Notice>
      )}
      {batch === null && !error && <div className="spinner">Loading accounts…</div>}

      {batch && !wrongClient && (
        <div className="table-wrap">
          <table>
            <tbody>
              {batch.accounts.map((account) => {
                const elsewhere =
                  account.linked_client_slug !== null && account.linked_client_slug !== slug;
                const here = account.linked_client_slug === slug;
                return (
                  <tr key={account.external_id} style={{ opacity: elsewhere ? 0.55 : 1 }}>
                    <td style={{ width: 28 }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        disabled={elsewhere}
                        checked={chosen.has(account.external_id)}
                        onChange={() => toggle(account.external_id)}
                        aria-label={account.external_name ?? account.external_id}
                      />
                    </td>
                    <td>
                      <strong>
                        {batch.platform === 'instagram' ? '@' : ''}
                        {account.external_name ?? account.external_id}
                      </strong>
                      <span className="muted mono truncate">{account.external_id}</span>
                    </td>
                    <td className="muted">
                      {here
                        ? 'Already connected here (ticking refreshes its token)'
                        : elsewhere
                          ? `Belongs to ${account.linked_client_name}`
                          : 'Not connected'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="primary shrink"
          disabled={chosen.size === 0 || busy || wrongClient}
          onClick={confirm}
        >
          {busy ? 'Connecting…' : `Connect ${chosen.size || ''} selected`}
        </button>
        <button className="shrink" onClick={cancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
